import { useMemo, useState } from "react";
import type { ConnectionGraph, ConnectionNode } from "@hub/shared";
import { Button, EmptyState, Pill, Spinner } from "@/ui";
import { useEngineQuery } from "@/engine/hooks";
import { useOpenContact } from "@/app/navigation";
import { tierLabel, tierTone } from "@/features/people/tier";
import { radialLayout, whyText } from "./radial";

const TIER_COLOR: Record<number, string> = { 1: "var(--tier-closest)", 2: "var(--tier-closer)", 3: "var(--tier-close)", 4: "var(--border-strong)" };

function initialsOf(name: string): string {
  return name.split(/\s+/).filter(Boolean).map((p) => p[0]).join("").slice(0, 2).toUpperCase() || "?";
}

function safeId(id: string): string {
  return id.replace(/[^a-z0-9]/gi, "_");
}

/**
 * One person's connections as a radial map: them in the middle, the people they share meetings, emails,
 * related-people labels, groups and Hub links with around them, sized and ranked by how much. Hover a node for the
 * why; click one to make it the centre.
 */
export function ConnectionsGraph({ contactId, limit = 24, height = 520, onCenter, onOpen }: { contactId: string; limit?: number; height?: number; onCenter: (contactId: string) => void; onOpen?: (contactId: string) => void }) {
  const open = useOpenContact();
  const graph = useEngineQuery("getConnectionGraph", { contactId, limit });
  const [hover, setHover] = useState<string | null>(null);
  // The drawing box follows the height, so a short section still gets a full ring rather than a small one in a wide box.
  const H = Math.max(320, Math.round(height));
  const W = Math.round(H * 1.55);
  const layout = useMemo(() => (graph.data ? radialLayout(graph.data.nodes.map((n) => ({ id: n.contact.contactId, weight: n.weight })), W, H) : null), [graph.data, H]);
  if (graph.isLoading) return <div style={{ display: "flex", justifyContent: "center", padding: 40 }}><Spinner /></div>;
  if (!graph.data || !layout) return <EmptyState title="No connections map">Pick someone to start from.</EmptyState>;
  const g = graph.data;
  const byId = new Map(g.nodes.map((n) => [n.contact.contactId, n]));
  const place = new Map(layout.nodes.map((n) => [n.id, n]));
  const maxLink = Math.max(1, ...g.links.map((l) => l.count));
  const maxWeight = Math.max(1, ...g.nodes.map((n) => n.weight));
  const hovered = hover ? byId.get(hover) ?? null : null;
  const hoveredPlace = hover ? place.get(hover) ?? null : null;
  const openPerson = onOpen ?? ((id: string) => open(id));
  return (
    <div style={{ position: "relative", width: "100%" }}>
      {g.nodes.length === 0 ? (
        <EmptyState title={g.isSelf ? "No connections yet" : `No connections for ${g.center.displayName} yet`}>
          {g.isSelf ? "Meetings and emails from your Mac, related people and groups draw this map." : "People on the same meetings and emails, related people and shared groups appear here."}
        </EmptyState>
      ) : (
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: "block", maxHeight: height }} role="img" aria-label={`Connections of ${g.center.displayName}`} onMouseLeave={() => setHover(null)}>
          <defs>
            {[g.center, ...g.nodes.map((n) => n.contact)].filter((c) => c.avatarUrl).map((c) => (
              <clipPath key={c.contactId} id={`cg-${safeId(c.contactId)}`}>
                <circle cx={c.contactId === g.center.contactId ? layout.center.x : place.get(c.contactId)?.x} cy={c.contactId === g.center.contactId ? layout.center.y : place.get(c.contactId)?.y} r={c.contactId === g.center.contactId ? layout.center.r : place.get(c.contactId)?.r} />
              </clipPath>
            ))}
          </defs>
          {/* Links among the ring: who on it knows whom. */}
          {g.links.map((l) => {
            const a = place.get(l.a);
            const b = place.get(l.b);
            if (!a || !b) return null;
            const lit = hover === l.a || hover === l.b;
            return <line key={`${l.a}-${l.b}`} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={lit ? "var(--accent)" : "var(--text-tertiary)"} strokeOpacity={lit ? 0.7 : 0.25} strokeWidth={0.75 + (l.count / maxLink) * 2.5} />;
          })}
          {/* Spokes from the centre, thicker the more they share. */}
          {layout.nodes.map((p) => {
            const n = byId.get(p.id)!;
            const lit = hover === p.id;
            return <line key={`spoke-${p.id}`} x1={layout.center.x} y1={layout.center.y} x2={p.x} y2={p.y} stroke={lit ? "var(--accent)" : "var(--text-secondary)"} strokeOpacity={lit ? 0.9 : 0.35} strokeWidth={1 + (n.weight / maxWeight) * 5} strokeLinecap="round" />;
          })}
          {/* The centre. */}
          <g style={{ cursor: "pointer" }} onClick={() => openPerson(g.center.contactId)}>
            <circle cx={layout.center.x} cy={layout.center.y} r={layout.center.r + 3} fill="var(--bg-elevated)" stroke={TIER_COLOR[g.center.bucket]} strokeWidth={3} />
            {g.center.avatarUrl ? (
              <image href={g.center.avatarUrl} x={layout.center.x - layout.center.r} y={layout.center.y - layout.center.r} width={layout.center.r * 2} height={layout.center.r * 2} clipPath={`url(#cg-${safeId(g.center.contactId)})`} preserveAspectRatio="xMidYMid slice" />
            ) : (
              <>
                <circle cx={layout.center.x} cy={layout.center.y} r={layout.center.r} fill="var(--accent)" />
                <text x={layout.center.x} y={layout.center.y} dy={layout.center.r * 0.36} textAnchor="middle" fontSize={layout.center.r} fontWeight={700} fill="var(--text-inverse)">{initialsOf(g.center.displayName)}</text>
              </>
            )}
            <text x={layout.center.x} y={layout.center.y + layout.center.r + 16} textAnchor="middle" fontSize={13} fontWeight={600} fill="var(--text)">{g.isSelf ? "You" : g.center.displayName}</text>
            <title>{g.center.displayName}</title>
          </g>
          {/* The ring. */}
          {layout.nodes.map((p) => {
            const n = byId.get(p.id)!;
            const c = n.contact;
            const lit = hover === p.id;
            return (
              <g key={p.id} style={{ cursor: "pointer" }} onMouseEnter={() => setHover(p.id)} onFocus={() => setHover(p.id)} onClick={() => onCenter(p.id)} onDoubleClick={() => openPerson(p.id)} tabIndex={0} role="button" aria-label={`${c.displayName}: ${whyText(n, g.isSelf)}`}>
                <circle cx={p.x} cy={p.y} r={p.r + 2.5} fill="var(--bg-elevated)" stroke={lit ? "var(--accent)" : TIER_COLOR[c.bucket]} strokeWidth={lit ? 3 : 2.5} />
                {c.avatarUrl ? (
                  <image href={c.avatarUrl} x={p.x - p.r} y={p.y - p.r} width={p.r * 2} height={p.r * 2} clipPath={`url(#cg-${safeId(c.contactId)})`} preserveAspectRatio="xMidYMid slice" />
                ) : (
                  <>
                    <circle cx={p.x} cy={p.y} r={p.r} fill={n.onHub ? "var(--success)" : "var(--bg-sunken)"} />
                    <text x={p.x} y={p.y} dy={p.r * 0.36} textAnchor="middle" fontSize={Math.max(9, p.r * 0.9)} fontWeight={600} fill={n.onHub ? "var(--text-inverse)" : "var(--text)"}>{initialsOf(c.displayName)}</text>
                  </>
                )}
                <text x={p.x} y={p.y + p.r + 13} textAnchor="middle" fontSize={11} fill={lit ? "var(--text)" : "var(--text-secondary)"} style={{ pointerEvents: "none" }}>{c.displayName.length > 18 ? c.displayName.slice(0, 17) + "…" : c.displayName}</text>
                <title>{whyText(n, g.isSelf)}</title>
              </g>
            );
          })}
        </svg>
      )}
      {hovered && hoveredPlace && (
        <div
          role="tooltip"
          style={{ position: "absolute", left: `${(hoveredPlace.x / W) * 100}%`, top: `${(hoveredPlace.y / H) * 100}%`, transform: `translate(-50%, ${hoveredPlace.y > H / 2 ? "calc(-100% - 36px)" : "28px"})`, background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: "var(--radius-md)", boxShadow: "var(--shadow-md)", padding: "8px 10px", maxWidth: 260, pointerEvents: "none", zIndex: 2 }}
        >
          <div className="u-row" style={{ gap: 6, fontWeight: 600, fontSize: 13 }}>
            {hovered.contact.displayName}
            {hovered.contact.bucket < 4 && <Pill tone={tierTone(hovered.contact.bucket)}>{tierLabel(hovered.contact.bucket)}</Pill>}
          </div>
          <div className="u-small u-muted" style={{ marginTop: 2 }}>{whyText(hovered, g.isSelf)}</div>
          <div className="u-small u-muted" style={{ marginTop: 4 }}>Click to centre, double-click to open</div>
        </div>
      )}
      {g.more > 0 && <div className="u-small u-muted" style={{ textAlign: "center", marginTop: 4 }}>{g.more} more {g.more === 1 ? "person" : "people"} with a smaller share.</div>}
    </div>
  );
}

/** The ring as a list: name, tier, why; the same facts for anyone who would rather read than hover. */
export function ConnectionsList({ graph, onCenter }: { graph: ConnectionGraph; onCenter: (contactId: string) => void }) {
  const open = useOpenContact();
  return (
    <div>
      {graph.nodes.map((n: ConnectionNode) => (
        <div key={n.contact.contactId} className="u-row" style={{ gap: 8, padding: "6px 8px", alignItems: "flex-start" }}>
          <button type="button" onClick={() => onCenter(n.contact.contactId)} style={{ flex: 1, textAlign: "left", border: "none", background: "transparent", cursor: "pointer", color: "inherit", padding: 0 }} title="Centre on them">
            <div className="u-row" style={{ gap: 6 }}>
              <span className="u-truncate" style={{ fontWeight: 500 }}>{n.contact.displayName}</span>
              {n.contact.bucket < 4 && <Pill tone={tierTone(n.contact.bucket)}>{tierLabel(n.contact.bucket)}</Pill>}
            </div>
            <div className="u-small u-muted">{whyText(n, graph.isSelf)}</div>
          </button>
          <Button size="sm" variant="ghost" onClick={() => open(n.contact.contactId)}>Open</Button>
        </div>
      ))}
    </div>
  );
}
